// Dedicated ROUND-TRIP route form for the charter webchat.
//
// A round trip is EXACTLY two legs and the form enforces that — there is no "add another leg":
//   • Leg 1 — Outbound: origin → (optional stops) → destination
//   • Leg 2 — Return:   return pickup → (optional stops) → the ORIGINAL departure (locked)
// The return DESTINATION is locked to the outbound origin (that's what makes it a round trip). The
// return PICKUP defaults to the outbound destination but is editable (the group may board somewhere
// else for the ride home). Passenger count + a free-text coach-disposition note are shared.
//
// On Confirm it calls onConfirm(routeText, token) where `token` is the { outbound, return,
// passengerCount, coachDisposition } payload CustomerApp appends as `[[ROUTE={<compact-json>}]]`.
// The agents side reads it via _route_verified_slots (the outbound/return branch). Field names
// (outbound/return.pickup/destination/stops/departDateTime; passengerCount; coachDisposition) are the
// web→agents contract for the round-trip form — do NOT rename.
//
// Reuses the exact endpoint/stop UI (LegEndpoint -> PlaceField) and styling from
// RouteFormMulti/RoutePopup, so behavior matches the other forms.

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/primitives';
import { cx } from '../ui/cx';
import {
  INPUT_BASE, Req, todayISO, combineDateTime, coarsePlace, hasEndpoint, LegEndpoint,
} from './RouteFormMulti';
import { PlaceField, tokenPlace, placePhrase } from './RoutePopup';
import { stateInvalid, duplicateStopIndices, dedupeStops, returnBeforeOutbound } from './validation';

function blankLeg() {
  return { from: null, to: null, departDate: '', departTime: '', stops: [] };
}

// Builds the round-trip token payload. Pure + exported so it's unit-testable. The return DESTINATION is
// forced to the outbound ORIGIN (the round-trip invariant) regardless of anything typed.
export function buildRoundTripToken(outbound, ret, passengerCount, coachDisposition) {
  const originPlace = outbound.from || {};
  return {
    outbound: {
      pickup: tokenPlace(outbound.from || {}),
      destination: tokenPlace(outbound.to || {}),
      // keepCoarseLabel: a stop is carried by its address alone (blank => the agents drop it).
      stops: dedupeStops((outbound.stops || []).filter(Boolean)).map((s) => tokenPlace(s, { keepCoarseLabel: true })),
      departDateTime: combineDateTime(outbound.departDate, outbound.departTime),
    },
    return: {
      pickup: tokenPlace(ret.from || {}),
      destination: tokenPlace(originPlace),          // LOCKED = the original departure
      stops: dedupeStops((ret.stops || []).filter(Boolean)).map((s) => tokenPlace(s, { keepCoarseLabel: true })),
      departDateTime: combineDateTime(ret.departDate, ret.departTime),
    },
    passengerCount: String(passengerCount || '').trim(),
    coachDisposition: String(coachDisposition || '').trim(),
  };
}

// A compact per-leg stops editor — each stop is the SAME single place search as the endpoints
// (PlaceField), so the whole form reads as one system. Matches RouteFormMulti's stops.
function StopsEditor({ legLabel, stops, setStops }) {
  const add = () => setStops([...(stops || []), null]);
  const setAt = (j, place) => setStops((stops || []).map((s, k) => (k === j ? place : s)));
  const remove = (j) => setStops((stops || []).filter((_, k) => k !== j));
  return (
    <div className="mt-5">
      <div className="mb-1.5 text-10 font-semibold uppercase tracking-brand text-peoria-muted">
        Stops along the way (optional)
      </div>
      {(stops || []).length > 0 && (
        <div className="space-y-3">
          {(stops || []).map((stop, j) => (
            <div key={j} className="flex items-start gap-2">
              <div className="flex-1 space-y-1.5">
                <PlaceField
                  label={`Stop ${j + 1}`}
                  required={false}
                  ariaTag={`${legLabel} stop ${j + 1}`}
                  place={stop}
                  placeholder="A stop along the way"
                  onSelect={(p) => setAt(j, { ...p })}
                  onClear={() => setAt(j, null)}
                  onCity={(v) => setAt(j, { ...(stop || {}), city: v })}
                  onState={(v) => setAt(j, { ...(stop || {}), state: v.toUpperCase().slice(0, 2) })}
                />
                {duplicateStopIndices(stops).has(j) && (
                  <div className="text-11 text-danger">Duplicate stop — it won't be added twice.</div>
                )}
              </div>
              <button type="button" onClick={() => remove(j)}
                aria-label={`Remove ${legLabel} stop ${j + 1}`}
                className="mt-7 rounded-btn p-1 text-peoria-muted hover:bg-peoria-surface2 hover:text-danger">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={add} aria-label={`Add a stop to the ${legLabel} leg`}
        className="mt-2 inline-flex items-center gap-1 text-13 font-medium text-peoria-red hover:opacity-80">
        <Plus size={14} /> Add stop
      </button>
    </div>
  );
}

export default function RouteFormRoundTrip({ isOpen, onClose, onConfirm, initial }) {
  const seed = initial || {};
  const [outbound, setOutbound] = useState(() => ({ ...blankLeg(), ...(seed.outbound || {}) }));
  const [ret, setRet] = useState(() => ({ ...blankLeg(), ...(seed.return || {}) }));
  const [passengerCount, setPassengerCount] = useState(() => String(seed.passengerCount || ''));
  const [coachDisposition, setCoachDisposition] = useState(() => String(seed.coachDisposition || ''));

  // Endpoint patch helpers (mirror RouteFormMulti.patchLoc / setLeg).
  const patchLoc = (setter, side, patch) => setter((l) => {
    const base = l[side] || coarsePlace();
    return { ...l, [side]: { ...base, ...patch } };
  });
  // A picked place REPLACES the endpoint outright: one search supplies the venue name, street, city and
  // state together, so carrying a previous `name` forward would strand a stale venue over a different
  // address (picking "2360 95th St" kept "Patel Brothers", a mile away). The old four-input endpoint
  // HAD to preserve it — the Location name was a separate box the rider typed — but that field is gone.
  // Mirrors RouteFormMulti's LegEndpoint handlers.
  const setEndpoint = (setter, side, place) => setter((l) => ({ ...l, [side]: place ? { ...place } : null }));

  // The return destination is LOCKED to the outbound origin — the round-trip invariant. A readable label.
  const phraseOf = (p) => (p ? placePhrase(p).replace(/^,\s*/, '') : '');
  const originLabel = phraseOf(outbound.from) || 'the original departure';

  // Nights the driver is committed between the outbound and return dates (the whole-day gap). Drives the
  // "driver hotel: N nights" note — a multi-day round trip auto-includes an estimated driver lodging cost.
  const nights = (() => {
    const o = (outbound.departDate || '').trim();
    const r = (ret.departDate || '').trim();
    if (!o || !r) return 0;
    const ms = Date.parse(`${r}T00:00:00`) - Date.parse(`${o}T00:00:00`);
    const d = Math.round(ms / 86400000);
    return Number.isFinite(d) && d > 0 ? d : 0;
  })();

  // The return leg must not depart before the outbound leg — a genuine correctness guard (the old form
  // only clamped the driver-hotel `nights` to 0 and let an earlier return through).
  const returnTooEarly = returnBeforeOutbound(
    combineDateTime(outbound.departDate, outbound.departTime),
    combineDateTime(ret.departDate, ret.departTime));
  // Every typed state must be a real 2-letter code, and no stop may repeat another on its leg.
  const legStatesOk = (l) => !stateInvalid(l.from?.state) && !stateInvalid(l.to?.state)
    && (l.stops || []).every((s) => !s || !stateInvalid(s.state));
  const noDupStops = (l) => duplicateStopIndices((l.stops || []).filter(Boolean)).size === 0;

  const canConfirm = hasEndpoint(outbound.from) && hasEndpoint(outbound.to) && !!outbound.departDate
    && hasEndpoint(ret.from) && !!ret.departDate && !!String(passengerCount).trim()
    && !returnTooEarly
    && legStatesOk(outbound) && legStatesOk(ret) && noDupStops(outbound) && noDupStops(ret);

  const confirm = () => {
    if (!canConfirm) return;
    const stopPhrase = (s) => {
      if (!s) return '';
      const name = String(s.name || '').trim();
      const cs = [s.city, s.state].filter(Boolean).join(', ');
      return (name && cs) ? `${name}, ${cs}` : (name || cs || String(s.label || '').trim());
    };
    const via = (stops) => {
      const list = (stops || []).map(stopPhrase).filter(Boolean);
      return list.length ? ` (via ${list.join(', ')})` : '';
    };
    const outText = `${phraseOf(outbound.from)}${via(outbound.stops)} → ${phraseOf(outbound.to)}`;
    const retText = `${phraseOf(ret.from)}${via(ret.stops)} → ${originLabel}`;
    const routeText = `My round-trip itinerary — Outbound: ${outText}; Return: ${retText}.`;
    // Force the return destination = the outbound origin before building the token.
    const retLocked = { ...ret, to: outbound.from };
    const token = buildRoundTripToken(outbound, retLocked, passengerCount, coachDisposition);
    onConfirm(routeText, token);
  };

  const footer = (
    <>
      <Button variant="peoriaOutline" onClick={onClose}>Cancel</Button>
      <Button variant="peoria" onClick={confirm} disabled={!canConfirm}>Confirm itinerary</Button>
    </>
  );

  const DateTime = ({ leg, setter, label }) => (
    <div className="mt-5 grid grid-cols-2 gap-3 md:max-w-md">
      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-peoria-text">{label} date<Req /></span>
        <input type="date" className={INPUT_BASE} min={todayISO()} value={leg.departDate}
          aria-label={`${label} date`} onChange={(e) => setter((l) => ({ ...l, departDate: e.target.value }))} />
      </label>
      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-peoria-text">{label} time</span>
        <input type="time" className={INPUT_BASE} value={leg.departTime}
          aria-label={`${label} time`} onChange={(e) => setter((l) => ({ ...l, departTime: e.target.value }))} />
      </label>
    </div>
  );

  return (
    <Modal open={isOpen} onClose={onClose} title="Your round-trip itinerary" size="wide" footer={footer} tone="peoria">
      <div className="mb-5 rounded-card border border-peoria-red/30 bg-peoria-red/10 px-4 py-3 text-13 text-peoria-muted">
        <strong className="text-peoria-text">Don't know the exact address yet? No problem.</strong>{' '}
        The city and state are enough for a preliminary estimate. Review both legs below — the return
        trip ends back at your original departure.
      </div>

      {/* Shared — passenger count */}
      <div className="mb-5 grid grid-cols-2 gap-3 md:max-w-xs">
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-peoria-text">Passengers<Req /></span>
          <input type="number" min="1" className={INPUT_BASE} value={passengerCount}
            aria-label="Passenger count" placeholder="e.g. 40"
            onChange={(e) => setPassengerCount(e.target.value)} />
        </label>
      </div>

      <ol className="space-y-5">
        {/* LEG 1 — OUTBOUND */}
        <li className="rounded-card border border-peoria-border bg-peoria-surface2/40 p-4 sm:p-5">
          <div className="mb-4 text-11 font-semibold uppercase tracking-brand text-peoria-red">Leg 1 — Outbound</div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <LegEndpoint legNum={1} label="From" place={outbound.from}
              onCity={(v) => patchLoc(setOutbound, 'from', { city: v })}
              onState={(v) => patchLoc(setOutbound, 'from', { state: v.toUpperCase().slice(0, 2) })}
              onSelectAddr={(p) => setEndpoint(setOutbound, 'from', p)}
              onClearAddr={() => setEndpoint(setOutbound, 'from', null)} />
            <LegEndpoint legNum={1} label="To" place={outbound.to}
              onCity={(v) => patchLoc(setOutbound, 'to', { city: v })}
              onState={(v) => patchLoc(setOutbound, 'to', { state: v.toUpperCase().slice(0, 2) })}
              onSelectAddr={(p) => setEndpoint(setOutbound, 'to', p)}
              onClearAddr={() => setEndpoint(setOutbound, 'to', null)} />
          </div>
          <StopsEditor legLabel="outbound" stops={outbound.stops}
            setStops={(s) => setOutbound((l) => ({ ...l, stops: typeof s === 'function' ? s(l.stops) : s }))} />
          <DateTime leg={outbound} setter={setOutbound} label="Departure" />
        </li>

        {/* LEG 2 — RETURN */}
        <li className="rounded-card border border-peoria-border bg-peoria-surface2/40 p-4 sm:p-5">
          <div className="mb-4 text-11 font-semibold uppercase tracking-brand text-peoria-red">Leg 2 — Return</div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <LegEndpoint legNum={2} label="Return pickup" place={ret.from}
              onCity={(v) => patchLoc(setRet, 'from', { city: v })}
              onState={(v) => patchLoc(setRet, 'from', { state: v.toUpperCase().slice(0, 2) })}
              onSelectAddr={(p) => setEndpoint(setRet, 'from', p)}
              onClearAddr={() => setEndpoint(setRet, 'from', null)} />
            {/* Return destination is LOCKED to the original departure — displayed, not editable. */}
            <div className="space-y-2">
              <span className="block text-10 font-semibold uppercase tracking-brand text-peoria-muted">
                Return to (original departure)
              </span>
              <div className={cx(INPUT_BASE, 'flex items-center bg-peoria-surface/60 text-peoria-muted cursor-not-allowed')}
                aria-label="Return destination (locked to original departure)">
                {originLabel}
              </div>
              <p className="text-11 text-peoria-muted">
                A round trip returns to where it started. To end somewhere else, start a new chat and
                choose a multi-leg trip.
              </p>
            </div>
          </div>
          <StopsEditor legLabel="return" stops={ret.stops}
            setStops={(s) => setRet((l) => ({ ...l, stops: typeof s === 'function' ? s(l.stops) : s }))} />
          <DateTime leg={ret} setter={setRet} label="Return" />
          {returnTooEarly && (
            <div className="mt-2 text-11 text-danger">
              The return must be on or after the outbound departure.
            </div>
          )}
        </li>
      </ol>

      {/* Multi-day driver lodging: the whole-day gap between the dates -> nights the driver is hotel'd.
          Shown automatically so the rider sees the driver hotel is included in the estimate. */}
      {nights > 0 && (
        <div className="mt-5 rounded-card border border-peoria-red/30 bg-peoria-red/10 px-4 py-3 text-13 text-peoria-muted">
          <strong className="text-peoria-text">Driver hotel: {nights} night{nights !== 1 ? 's' : ''}.</strong>{' '}
          This is a multi-day trip, so we automatically include an estimated driver lodging cost for
          {' '}{nights} night{nights !== 1 ? 's' : ''} in the quote.
        </div>
      )}

      {/* Coach disposition — free-text, dispatcher-only (does not change the estimate). */}
      <div className="mt-5 space-y-1.5">
        <span className="text-11 font-semibold uppercase tracking-brand text-peoria-red">
          Between the trips {nights > 0 ? '— hotels & driver plan' : ''}(optional)
        </span>
        <textarea rows={2} className={cx(INPUT_BASE, 'resize-y')} value={coachDisposition}
          aria-label="Coach disposition"
          placeholder={nights > 0
            ? 'Do you have hotels figured out? Should the driver stay with the group between the trips, or head back?'
            : 'Should the coach stay with the group between the outbound and return trips, or can it leave and come back later?'}
          onChange={(e) => setCoachDisposition(e.target.value)} />
      </div>
    </Modal>
  );
}
